import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { GrowthPublishPlan } from './growth-marketing.service';

export interface GrowthStrategyActivationPlan {
  id: string;
  tenantId: string;
  strategyArtifactId: string;
  status: string;
  planningHorizonDays: number;
  objectiveJson: string;
  kpiTargetsJson: string;
  segmentRefsJson: string;
  offeringRefsJson: string;
  channelMixJson: string;
  contentPillarsJson: string;
  riskSnapshotJson: string;
  approvedByUserId?: string | null;
  approvedAtUtc?: string | null;
  workflowProcessInstanceId?: string | null;
  workflowIntentRequestId?: string | null;
  workflowIntentRouteFallbackReason?: string | null;
  lastWorkflowDeferredCode?: string | null;
  lastWorkflowDeferredMessage?: string | null;
  lastWorkflowDeferredAtUtc?: string | null;
  lastWorkflowIntegrationAttemptedAtUtc?: string | null;
  lastWorkflowIntegrationCorrelationId?: string | null;
  idempotencyKey: string;
  correlationId: string;
  createdAtUtc: string;
}

export interface GrowthPublishJob {
  id: string;
  tenantId: string;
  publishPlanId: string;
  contentAssetId: string;
  channelKey: string;
  status: string;
  providerMode: string;
  idempotencyKey: string;
  correlationId: string;
  strategyArtifactId?: string | null;
  activationPlanId?: string | null;
  attemptCount: number;
  lastError?: string | null;
  externalRef?: string | null;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
  createdAtUtc: string;
}

export interface GrowthStorefrontApplyRequest {
  id: string;
  tenantId: string;
  contentAssetId: string;
  targetType: string;
  seoProfileId?: string | null;
  offeringRefId?: string | null;
  requestPayloadJson: string;
  status: string;
  idempotencyKey: string;
  workflowProcessInstanceId?: string | null;
  workflowIntentRequestId?: string | null;
  workflowIntentRouteFallbackReason?: string | null;
  lastWorkflowDeferredCode?: string | null;
  lastWorkflowDeferredMessage?: string | null;
  lastWorkflowDeferredAtUtc?: string | null;
  lastWorkflowIntegrationAttemptedAtUtc?: string | null;
  lastWorkflowIntegrationCorrelationId?: string | null;
  correlationId: string;
  activationPlanId?: string | null;
  strategyArtifactId?: string | null;
  createdAtUtc: string;
}

export interface CalendarGeneratePreviewResult {
  previewToken: string;
  contentPlanId: string;
  items: Array<{
    id?: string;
    channelKey: string;
    contentType?: string;
    title?: string;
    scheduledAtUtc: string;
    status: string;
    strategyArtifactId?: string | null;
    activationPlanId?: string | null;
    sourceObjectiveKey?: string | null;
    riskLevel?: string | null;
  }>;
}

@Injectable({ providedIn: 'root' })
export class GrowthDistributionService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth`;

  listActivationPlans(): Observable<GrowthStrategyActivationPlan[]> {
    return this.http.get<GrowthStrategyActivationPlan[]>(`${this.base}/strategy/activation-plans`);
  }

  getActivationPlan(id: string): Observable<GrowthStrategyActivationPlan> {
    return this.http.get<GrowthStrategyActivationPlan>(`${this.base}/strategy/activation-plans/${id}`);
  }

  createActivationPlan(body: Record<string, unknown>): Observable<GrowthStrategyActivationPlan> {
    return this.http.post<GrowthStrategyActivationPlan>(`${this.base}/strategy/activation-plans`, body);
  }

  requestActivationPlanApproval(id: string): Observable<GrowthStrategyActivationPlan> {
    return this.http.post<GrowthStrategyActivationPlan>(`${this.base}/strategy/activation-plans/${id}/request-approval`, {});
  }

  approveActivationPlan(id: string): Observable<GrowthStrategyActivationPlan> {
    return this.http.post<GrowthStrategyActivationPlan>(`${this.base}/strategy/activation-plans/${id}/approve`, {});
  }

  previewCalendar(body: { activationPlanId: string; startUtc: string; cadenceDays?: number; idempotencyKey?: string }): Observable<CalendarGeneratePreviewResult> {
    return this.http.post<CalendarGeneratePreviewResult>(`${this.base}/marketing/calendar/generate-preview`, body);
  }

  commitCalendar(body: { activationPlanId: string; previewToken: string; cadenceDays?: number; idempotencyKey: string }): Observable<{ plan: unknown; items: unknown[]; isReplay: boolean }> {
    return this.http.post<{ plan: unknown; items: unknown[]; isReplay: boolean }>(`${this.base}/marketing/calendar/commit`, body);
  }

  generateAllBriefs(contentPlanId: string): Observable<{ created: number }> {
    return this.http.post<{ created: number }>(`${this.base}/marketing/content-calendar/${contentPlanId}/briefs/generate-all`, {});
  }

  createBriefFromCalendarItem(itemId: string, body: { idempotencyKey: string; correlationId?: string }): Observable<{ brief: unknown; isReplay: boolean }> {
    return this.http.post<{ brief: unknown; isReplay: boolean }>(`${this.base}/marketing/calendar/items/${itemId}/create-brief`, body);
  }

  listPublishJobs(status?: string, channelKey?: string): Observable<GrowthPublishJob[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (channelKey) params.set('channelKey', channelKey);
    const q = params.toString();
    return this.http.get<GrowthPublishJob[]>(`${this.base}/marketing/publish-operations/jobs${q ? `?${q}` : ''}`);
  }

  requestPublishPlanApproval(publishPlanId: string): Observable<GrowthPublishPlan> {
    return this.http.post<GrowthPublishPlan>(`${this.base}/marketing/publish-operations/publish-plans/${publishPlanId}/request-approval`, {});
  }

  approvePublishPlan(publishPlanId: string): Observable<GrowthPublishPlan> {
    return this.http.post<GrowthPublishPlan>(`${this.base}/marketing/publish-operations/publish-plans/${publishPlanId}/approve`, {});
  }

  executePublishPlan(publishPlanId: string, body: { idempotencyKey: string; correlationId?: string }): Observable<GrowthPublishJob[]> {
    return this.http.post<GrowthPublishJob[]>(`${this.base}/marketing/publish-operations/publish-plans/${publishPlanId}/execute`, body);
  }

  retryPublishJob(jobId: string, idempotencyKey: string): Observable<GrowthPublishJob> {
    return this.http.post<GrowthPublishJob>(`${this.base}/marketing/publish-operations/jobs/${jobId}/retry`, { idempotencyKey });
  }

  listStorefrontApplyRequests(): Observable<GrowthStorefrontApplyRequest[]> {
    return this.http.get<GrowthStorefrontApplyRequest[]>(`${this.base}/marketing/storefront-apply-requests`);
  }

  createStorefrontApplyRequest(body: Record<string, unknown>): Observable<GrowthStorefrontApplyRequest> {
    return this.http.post<GrowthStorefrontApplyRequest>(`${this.base}/marketing/storefront-apply-requests`, body);
  }

  requestStorefrontApplyApproval(id: string): Observable<GrowthStorefrontApplyRequest> {
    return this.http.post<GrowthStorefrontApplyRequest>(`${this.base}/marketing/storefront-apply-requests/${id}/request-approval`, {});
  }

  applyStorefrontRequest(id: string): Observable<GrowthStorefrontApplyRequest> {
    return this.http.post<GrowthStorefrontApplyRequest>(`${this.base}/marketing/storefront-apply-requests/${id}/apply`, {});
  }
}
